import React, { useState } from 'react';
import { Plus, Trash2, Zap, MapPin, Clock, Settings } from 'lucide-react';
import InfoTooltip from '../../../../components/InfoTooltip';

const Step4SurgeZones = ({ formData, onChange }) => {
  const [activeTab, setActiveTab] = useState('surge');

  const addSurgeMultiplier = () => {
    const newMultiplier = {
      timeRange: { start: '09:00', end: '17:00' },
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      type: 'multiplier',
      value: 1.5,
      zones: ['all']
    };

    onChange({
      surge: {
        ...formData.surge,
        multipliers: [...(formData.surge?.multipliers || []), newMultiplier]
      }
    });
  };

  const updateSurgeMultiplier = (index, updates) => {
    const newMultipliers = [...(formData.surge?.multipliers || [])];
    newMultipliers[index] = { ...newMultipliers[index], ...updates };
    onChange({
      surge: {
        ...formData.surge,
        multipliers: newMultipliers
      }
    });
  };

  const removeSurgeMultiplier = (index) => {
    const newMultipliers = (formData.surge?.multipliers || []).filter((_, i) => i !== index);
    onChange({
      surge: {
        ...formData.surge,
        multipliers: newMultipliers
      }
    });
  };

  const addZone = () => {
    const zoneName = prompt('Enter zone name:');
    if (zoneName) {
      const newZone = {
        type: 'multiplier',
        value: 1.0,
        baseFare: 50
      };

      onChange({
        zones: {
          ...formData.zones,
          [zoneName]: newZone
        }
      });
    }
  };

  const updateZone = (zoneName, updates) => {
    onChange({
      zones: {
        ...formData.zones,
        [zoneName]: { ...formData.zones[zoneName], ...updates }
      }
    });
  };

  const removeZone = (zoneName) => {
    const newZones = { ...formData.zones };
    delete newZones[zoneName];
    onChange({ zones: newZones });
  };

  const addCancellationFee = () => {
    const newFee = {
      timeWindow: 5,
      fee: 20
    };

    onChange({
      cancellationPolicy: {
        ...formData.cancellationPolicy,
        cancellationFees: [...(formData.cancellationPolicy?.cancellationFees || []), newFee]
      }
    });
  };

  const updateCancellationFee = (index, updates) => {
    const newFees = [...(formData.cancellationPolicy?.cancellationFees || [])];
    newFees[index] = { ...newFees[index], ...updates };
    onChange({
      cancellationPolicy: {
        ...formData.cancellationPolicy,
        cancellationFees: newFees
      }
    });
  };

  const removeCancellationFee = (index) => {
    const newFees = (formData.cancellationPolicy?.cancellationFees || []).filter((_, i) => i !== index);
    onChange({
      cancellationPolicy: {
        ...formData.cancellationPolicy,
        cancellationFees: newFees
      }
    });
  };

  const tabs = [
    { id: 'surge', name: 'Surge Pricing', icon: Zap },
    { id: 'zones', name: 'Zones', icon: MapPin },
    { id: 'cancellation', name: 'Cancellation Policy', icon: Settings }
  ];

  const days = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Surge Pricing & Zones</h4>
        <p className="text-sm text-gray-600">
          Configure surge pricing multipliers, geographic zones, and cancellation policies.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 inline mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Surge Pricing Tab */}
      {activeTab === 'surge' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Surge Pricing Multipliers</h5>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.surge?.enabled || false}
                  onChange={(e) => onChange({
                    surge: { ...formData.surge, enabled: e.target.checked }
                  })}
                  className="mr-2"
                />
                Enable Surge Pricing
              </label>
              <button
                onClick={addSurgeMultiplier}
                className="btn btn-sm btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Multiplier
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {(formData.surge?.multipliers || []).map((multiplier, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-900">Multiplier #{index + 1}</h6>
                  <button
                    onClick={() => removeSurgeMultiplier(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">
                      Time Range
                      <InfoTooltip text="Time period when this surge multiplier is active" />
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="time"
                        value={multiplier.timeRange?.start || ''}
                        onChange={(e) => updateSurgeMultiplier(index, {
                          timeRange: { ...multiplier.timeRange, start: e.target.value }
                        })}
                        className="input"
                      />
                      <span className="flex items-center text-gray-500">to</span>
                      <input
                        type="time"
                        value={multiplier.timeRange?.end || ''}
                        onChange={(e) => updateSurgeMultiplier(index, {
                          timeRange: { ...multiplier.timeRange, end: e.target.value }
                        })}
                        className="input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Days</label>
                    <div className="flex flex-wrap gap-2">
                      {days.map((day) => (
                        <label key={day} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={multiplier.days?.includes(day) || false}
                            onChange={(e) => {
                              const currentDays = multiplier.days || [];
                              const newDays = e.target.checked
                                ? [...currentDays, day]
                                : currentDays.filter(d => d !== day);
                              updateSurgeMultiplier(index, { days: newDays });
                            }}
                            className="mr-1"
                          />
                          <span className="text-sm capitalize">{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="How surge is applied: percentage increase, fixed amount, or multiplier" />
                    </label>
                    <select
                      value={multiplier.type}
                      onChange={(e) => updateSurgeMultiplier(index, { type: e.target.value })}
                      className="input"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="multiplier">Multiplier</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Value
                      <InfoTooltip text="Surge value. For multiplier, use 1.5 for 1.5x pricing" />
                    </label>
                    <input
                      type="number"
                      value={multiplier.value}
                      onChange={(e) => updateSurgeMultiplier(index, { value: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.1"
                      min="0"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Zones</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(formData.zones || {}).map((zone) => (
                        <label key={zone} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={multiplier.zones?.includes(zone) || false}
                            onChange={(e) => {
                              const currentZones = multiplier.zones || [];
                              const newZones = e.target.checked
                                ? [...currentZones, zone]
                                : currentZones.filter(z => z !== zone);
                              updateSurgeMultiplier(index, { zones: newZones });
                            }}
                            className="mr-1"
                          />
                          <span className="text-sm">{zone}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zones Tab */}
      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Geographic Zones</h5>
            <button
              onClick={addZone}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Zone
            </button>
          </div>

          <div className="space-y-4">
            {Object.entries(formData.zones || {}).map(([zoneName, zone]) => (
              <div key={zoneName} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium text-gray-900">{zoneName}</h6>
                  <button
                    onClick={() => removeZone(zoneName)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="How zone pricing is applied to base fare" />
                    </label>
                    <select
                      value={zone.type}
                      onChange={(e) => updateZone(zoneName, { type: e.target.value })}
                      className="input"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="multiplier">Multiplier</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Value
                      <InfoTooltip text="Zone pricing value based on selected type" />
                    </label>
                    <input
                      type="number"
                      value={zone.value}
                      onChange={(e) => updateZone(zoneName, { value: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.1"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Base Fare (₹)
                      <InfoTooltip text="Base fare override for this specific zone" />
                    </label>
                    <input
                      type="number"
                      value={zone.baseFare}
                      onChange={(e) => updateZone(zoneName, { baseFare: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancellation Policy Tab */}
      {activeTab === 'cancellation' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Cancellation Policy</h5>
            <button
              onClick={addCancellationFee}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Fee
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Free Cancellation Window (minutes)
                <InfoTooltip text="Time period after booking during which cancellation is free" />
              </label>
              <input
                type="number"
                value={formData.cancellationPolicy?.freeCancellationWindow || 0}
                onChange={(e) => onChange({
                  cancellationPolicy: {
                    ...formData.cancellationPolicy,
                    freeCancellationWindow: parseInt(e.target.value) || 0
                  }
                })}
                className="input"
                min="0"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h6 className="text-sm font-medium text-gray-900">Cancellation Fees</h6>
            {(formData.cancellationPolicy?.cancellationFees || []).map((fee, index) => (
              <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                <input
                  type="number"
                  value={fee.timeWindow || ''}
                  onChange={(e) => updateCancellationFee(index, { timeWindow: e.target.value ? parseInt(e.target.value) : null })}
                  className="input input-sm flex-1"
                  placeholder="Time Window (minutes)"
                  min="0"
                />
                <span className="text-gray-500">minutes</span>
                <input
                  type="number"
                  value={fee.fee}
                  onChange={(e) => updateCancellationFee(index, { fee: parseFloat(e.target.value) || 0 })}
                  className="input input-sm w-20"
                  placeholder="Fee"
                  step="0.01"
                  min="0"
                />
                <span className="text-sm text-gray-500">₹</span>
                <button
                  onClick={() => removeCancellationFee(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-800 mb-2">Configuration Summary</h5>
        <div className="text-sm text-blue-700 space-y-1">
          <p>Surge Multipliers: {(formData.surge?.multipliers || []).length}</p>
          <p>Zones: {Object.keys(formData.zones || {}).length}</p>
          <p>Cancellation Fees: {(formData.cancellationPolicy?.cancellationFees || []).length}</p>
        </div>
      </div>
    </div>
  );
};

export default Step4SurgeZones;
